import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import { useGetProductsQuery } from "../../redux/api/productsApi.js";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import ProductCard from "../product/ProductCard.jsx";
import {
  Navigation,
  Autoplay,
  Scrollbar,
  Keyboard,
  Mousewheel,
} from "swiper/modules";
import { useSelector, useDispatch } from "react-redux";
import { setCartItem } from "../../redux/slices/cartSlice";
import { openCart } from "../../redux/slices/uiSlice";
import { Helmet } from "react-helmet-async";

import BlogsSection from "./BlogSection.jsx";
import HeroSlider from "./ResponsiveHeroSlider.jsx";
import getErrorMessage from "../../utils/getErrorMessage.js";

// ─── Typography ─────────────────────────────────────────────────────────
//
//  Display / headings  → Playfair Display
//  Body / UI copy      → Source Sans 3
//  Labels / tags       → Courier New
//
//  Scale (mobile → desktop):
//    display   : clamp(1.75rem, 5vw, 3rem)     — hero-level headings
//    heading   : clamp(1.25rem, 3.5vw, 2rem)   — section titles
//    subheading: clamp(0.95rem, 2vw, 1.125rem) — supporting copy
//    label     : 0.65rem, tracking-widest       — tags, hints, captions
//
// ──────────────────────────────────────────────────────────────────────────────

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.12 },
  },
};

const childVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const dividerVariants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.6, ease: "easeOut", delay: 0.25 },
  },
};

const ctaVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.3 } },
};

const CtaLink = React.memo(({ to, href, children }) => {
  const inner = (
    <>
      <span className="relative z-10 transition-colors duration-300">
        {children}
      </span>
      <motion.span
        className="absolute inset-0 w-full h-full bg-[#BC3F4A]/10"
        initial={{ x: "-100%" }}
        whileHover={{ x: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      />
    </>
  );

  const cls =
    "relative inline-flex items-center justify-center gap-2 px-6 py-3 " +
    "font-sourcesans font-medium text-sm tracking-wide text-[#BC3F4A] " +
    "overflow-hidden transition-all duration-300 border border-[#BC3F4A]/30 " +
    "hover:border-[#BC3F4A]/60";

  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      transition={{ type: "spring", stiffness: 300, damping: 18 }}
    >
      {href ? (
        <a href={href} className={cls}>
          {inner}
        </a>
      ) : (
        <Link to={to} className={cls}>
          {inner}
        </Link>
      )}
    </motion.div>
  );
});

const SectionHeading = React.memo(({ title, body }) => (
  <motion.div
    className="text-center mb-8 px-6 md:px-12"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={sectionVariants}
  >
    {/* Label — Courier, uppercase, muted — consistent across all sections */}
    <motion.p
      className="font-mono text-[0.6rem] tracking-[0.3em] uppercase text-stone-400 mb-3"
      variants={childVariants}
    >
      sanguine accent
    </motion.p>

    {/* Display heading — Playfair, no bold, fluid size */}
    <motion.h2
      className="font-playfair font-normal text-stone-800 leading-tight"
      style={{ fontSize: "clamp(1.5rem, 4vw, 2.25rem)", textWrap: "balance" }}
      variants={childVariants}
    >
      {title}
    </motion.h2>

    {/* Supporting copy — Source Sans, regular weight, muted */}
    {body && (
      <motion.p
        className="mt-3 font-sourcesans font-normal text-stone-500 leading-relaxed max-w-xl mx-auto"
        style={{ fontSize: "clamp(0.875rem, 2vw, 1rem)", textWrap: "balance" }}
        variants={childVariants}
      >
        {body}
      </motion.p>
    )}

    {/* Animated rule */}
    <motion.div
      className="mt-6 w-12 h-[1.5px] bg-stone-300 mx-auto"
      variants={dividerVariants}
      style={{ originX: 0.5 }}
    />
  </motion.div>
));

// ─── Component ────────────────────────────────────────────────────────────────

const Home = () => {
  const { data, error, isError, isLoading, isFetching } = useGetProductsQuery();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const [srMsg, setSrMsg] = useState("");

  const errorToastShown = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.navigator?.userAgent?.includes?.("ReactSnap")) return;
    if (isError && !errorToastShown.current) {
      errorToastShown.current = true;
      toast.error(getErrorMessage(error));
    }
    if (!isError) errorToastShown.current = false;
  }, [isError, error]);

  const timerRef = useRef(null);

  const cartMap = useMemo(
    () => new Map(cartItems.map(i => [i.product, i])),
    [cartItems]
  );
  
  const addItemToCart = useCallback(
    (product) => {
      const existingCartItem = cartMap.get(product._id);
  
      const newQuantity = existingCartItem ? existingCartItem.quantity + 1 : 1;
  
      if (existingCartItem && newQuantity > (product?.stock ?? 0)) {
        toast.error("Maximum items already added");
        return;
      }
  
      dispatch(
        setCartItem({
          product: product?._id,
          name: product?.name,
          price: product?.price,
          image: product?.images?.[0]?.url,
          stock: product?.stock,
          quantity: newQuantity,
        })
      );
  
      dispatch(openCart());
  
      const msg = existingCartItem ? "Quantity updated" : "Added to bag";
      toast.success(msg);
  
      setSrMsg(`${product?.name ?? "Item"}: ${msg}`);
  
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setSrMsg(""), 1500);
    },
    [cartMap, dispatch] // ✅ IMPORTANT: dependency updated
  );

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const visibleProducts = useMemo(
    () => (data?.products ?? []).filter((p) => p?._id && p?.name),
    [data]
  );

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Sanguine Accent Home",
      url: "https://sanguineaccent.com/",
      description:
        "Handmade cards that give back. A blog about the things that matter day to day. Photo stories from the streets of New York and beyond.",
      hasPart: (data?.products ?? [])
        .slice(0, 10)
        .filter((p) => p?._id && p?.name && p?.images?.[0]?.url)
        .map((product) => ({
          "@type": "Product",
          name: product.name,
          image: product.images[0].url,
          url: `https://sanguineaccent.com/product/${product._id}`,
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price: product.price ?? 0,
            availability:
              product.stock > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
          },
        })),
    }),
    [data]
  );
  const safeJsonLd = useMemo(
    () => JSON.stringify(jsonLd).replace(/<\/script>/gi, "<\\/script>"),
    [jsonLd]
  );

  const swiperModules = useMemo(() => [
    Navigation,
    Autoplay,
    Scrollbar,
    Keyboard,
    Mousewheel,
  ], []);

  return (
    <>
      <Helmet>
        <title>
          Sanguine Accent — Creating Beauty With Honesty and Purpose
        </title>
        <meta
          name="description"
          content="Handmade string art cards, honest writing, and photo stories from New York. 70% of every card purchase goes directly to people serving in Ukraine."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://sanguineaccent.com/" />
        <meta
          property="og:title"
          content="Sanguine Accent — Creating Beauty With Honesty and Purpose"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:description"
          content="Handmade string art cards, honest writing, and photo stories from New York. 70% of every card purchase goes directly to people serving in Ukraine."
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/ducc0wskb/image/upload/v1771983355/blogs/zfxcgtwyhwkyogtpim26.webp"
        />
        <meta property="og:url" content="https://www.sanguineaccent.com/" />
        <script type="application/ld+json">{safeJsonLd}</script>
      </Helmet>

      <div aria-live="polite" className="sr-only" role="status">
        {srMsg}
      </div>

      <div className="bg-[#f5f3ea] pb-20 min-h-screen">
    
        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <HeroSlider />

        {/* ── Mission ──────────────────────────────────────────────────────── */}
        <section className="py-16 px-6 md:px-12">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={sectionVariants}
          >
            {/* Eyebrow label */}
            <motion.p
              className="font-mono text-[0.6rem] tracking-[0.3em] uppercase text-stone-400 mb-3"
              variants={childVariants}
            >
              our mission
            </motion.p>

            {/* Playfair display heading — italic for warmth */}
            <motion.h2
              className="font-playfair font-normal italic text-stone-800 leading-tight"
              style={{
                fontSize: "clamp(1.5rem, 4vw, 2.25rem)",
                textWrap: "balance",
              }}
              variants={childVariants}
            >
              Creating beauty with honesty and purpose
            </motion.h2>

            <motion.p
              className="mt-4 font-sourcesans font-normal text-stone-500 leading-relaxed"
              style={{
                fontSize: "clamp(0.875rem, 2vw, 1rem)",
                textWrap: "balance",
              }}
              variants={childVariants}
            >
              String art made by hand. Writing that means something. Photography
              that pays attention. And 70% of every card purchase going straight
              to people serving in Ukraine.
            </motion.p>

            <motion.div
              className="mt-6 w-12 h-[1.5px] bg-stone-300 mx-auto"
              variants={dividerVariants}
              style={{ originX: 0.5 }}
            />
          </motion.div>
        </section>

        {/* ── Collection ───────────────────────────────────────────────────── */}
        <section className="py-10 border-t border-stone-200">
          <div className="max-w-6xl mx-auto">
            <SectionHeading
              title="Cards made by hand"
              body="Each card is handmade. 70% of your purchase goes straight to people I trust in Ukraine."
            />

            {isLoading || isFetching ? (
              <div className="px-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl bg-stone-200 aspect-[4/5]"
                  />
                ))}
              </div>
            ) : isError ? (
              <p className="text-center text-stone-500">
                Could not load products. Please try again later.
              </p>
            ) : visibleProducts.length === 0 ? (
              <p
                className="text-center font-sourcesans text-stone-500 px-6"
                style={{ fontSize: "clamp(0.875rem, 2vw, 1rem)" }}
              >
                No items available yet.
              </p>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                {/*
                 * overflow-hidden on the wrapper prevents horizontal page
                 * scroll on mobile. Swiper needs !overflow-visible so peeking
                 * slides aren't clipped — but that bleed is safely contained
                 * by this parent. The px-4 insets the first card so it's not
                 * flush to the screen edge, while the peek bleeds right.
                 */}
                <div className="overflow-hidden px-4 md:px-0">
                  <Swiper
                    modules={swiperModules}
                    breakpoints={{
                      0: { slidesPerView: 1.15, spaceBetween: 12 },
                      375: { slidesPerView: 1.85, spaceBetween: 12 },
                      480: { slidesPerView: 2.2, spaceBetween: 14 },
                      640: { slidesPerView: 2.5, spaceBetween: 16 },
                      768: { slidesPerView: 3, spaceBetween: 16 },
                      1024: { slidesPerView: 4, spaceBetween: 20 },
                    }}
                    autoplay={{
                      delay: 3500,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    scrollbar={{ hide: false, draggable: true }}
                    keyboard={{ enabled: true }}
                    mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
                    navigation={{
                      nextEl: ".home-swiper-next",
                      prevEl: ".home-swiper-prev",
                    }}
                    className="w-full !overflow-visible"
                  >
                    {visibleProducts.map((product) => (
                      <SwiperSlide key={product._id}>
                        <ProductCard
                          product={product}
                          addItemToCart={addItemToCart}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>

                {/* Swipe hint — mobile only */}
                <p className="md:hidden text-center font-mono text-[0.6rem] tracking-[0.25em] uppercase text-stone-400 mt-3 select-none">
                  swipe to browse
                </p>

                {/* Desktop nav arrows */}
                <div className="hidden md:flex justify-end gap-2 mt-4 px-12">
                  <button
                    className="home-swiper-prev w-9 h-9 flex items-center justify-center rounded-full border border-stone-300 text-stone-500 hover:border-stone-600 hover:text-stone-800 transition text-sm disabled:opacity-30"
                    aria-label="Previous products"
                  >
                    ←
                  </button>
                  <button
                    className="home-swiper-next w-9 h-9 flex items-center justify-center rounded-full border border-stone-300 text-stone-500 hover:border-stone-600 hover:text-stone-800 transition text-sm disabled:opacity-30"
                    aria-label="Next products"
                  >
                    →
                  </button>
                </div>
              </motion.div>
            )}

            <div className="text-center mt-10 px-6">
              <CtaLink to="/supportUkraine">
                Explore the full collection →
              </CtaLink>
            </div>
          </div>
        </section>

        <BlogsSection />

        {/* ── Connect ──────────────────────────────────────────────────────── */}
        <section className="py-16 px-6 md:px-12 border-t border-stone-200">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={sectionVariants}
          >
            {/* Eyebrow */}
            <motion.p
              className="font-mono text-[0.6rem] tracking-[0.3em] uppercase text-stone-400 mb-3"
              variants={childVariants}
            >
              get in touch
            </motion.p>

            <motion.h2
              className="font-playfair font-normal text-stone-800 leading-tight"
              style={{
                fontSize: "clamp(1.5rem, 4vw, 2.25rem)",
                textWrap: "balance",
              }}
              variants={childVariants}
            >
              Say hello
            </motion.h2>

            <motion.p
              className="mt-4 font-sourcesans font-normal text-stone-500 leading-relaxed"
              style={{
                fontSize: "clamp(0.875rem, 2vw, 1rem)",
                textWrap: "balance",
              }}
              variants={childVariants}
            >
              A question about a card, a collaboration idea, or just want to
              reach out — I'd love to hear from you.
            </motion.p>

            <motion.div
              className="mt-6 w-12 h-[1.5px] bg-stone-300 mx-auto"
              variants={dividerVariants}
              style={{ originX: 0.5 }}
            />

            <motion.div
              className="mt-8 flex flex-col sm:flex-row justify-center gap-4"
              variants={ctaVariants}
            >
              <CtaLink to="/contact">Let&apos;s Connect</CtaLink>
              <CtaLink href="mailto:sanguineaccent@gmail.com">Email Us</CtaLink>
            </motion.div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Home;
